import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import useLogActivityAccess from '../hooks/useLogActivityAccess';
import './DicionarioFaminto.css';

const DAILY_LIMIT = 3;

function playSound(src) {
  try { new Audio(src).play(); } catch {}
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export default function DicionarioFaminto() {
  useLogActivityAccess('dicionario-faminto');
  const { user } = useAuth();
  const [screen,     setScreen]     = useState('start');  // start | main
  const [tab,        setTab]        = useState('add');    // add | list | quiz
  const [palavras,   setPalavras]   = useState([]);
  const [en,         setEn]         = useState('');
  const [pt,         setPt]         = useState('');
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [todayCount, setTodayCount] = useState(0);
  const [toast,      setToast]      = useState(null);

  // Quiz
  const [quizWords,     setQuizWords]     = useState([]);
  const [quizIndex,     setQuizIndex]     = useState(0);
  const [quizOptions,   setQuizOptions]   = useState([]);
  const [quizAnswered,  setQuizAnswered]  = useState(null);
  const [quizScore,     setQuizScore]     = useState(0);
  const [quizFeedback,  setQuizFeedback]  = useState('');
  const [quizMode,      setQuizMode]      = useState('en→pt'); // 'en→pt' | 'pt→en'
  const [quizDone,      setQuizDone]      = useState(false);

  const toastTimer = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Carregar palavras ─────────────────────────────────────
  async function loadPalavras() {
    if (!user) return;
    try {
      const res = await fetch(`/user/listar-palavras/${user.id}`, { credentials: 'include' });
      const data = await res.json();
      setPalavras(Array.isArray(data) ? data : []);
      // conta de hoje
      const today = new Date().toISOString().slice(0, 10);
      const count = data.filter(p => p.data_adicao?.slice(0, 10) === today).length;
      setTodayCount(count);
    } catch { setPalavras([]); }
  }

  useEffect(() => {
    if (screen === 'main') loadPalavras();
  }, [screen]); // eslint-disable-line

  // ── Adicionar palavra ─────────────────────────────────────
  async function adicionarPalavra(e) {
    e.preventDefault();
    if (!en.trim() || !pt.trim()) { showToast('Preencha os dois campos!', 'warning'); return; }
    if (todayCount >= DAILY_LIMIT) { showToast(`Limite diário de ${DAILY_LIMIT} palavras atingido!`, 'error'); return; }
    setLoading(true);
    try {
      const res = await fetch('/user/adicionar-palavra', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userlogin_id: user.id, palavra_ingles: en.trim(), palavra_pt: pt.trim() }),
      });
      const data = await res.json();
      if (data.message?.includes('sucesso')) {
        playSound('/sound/applause.mp3');
        showToast('Palavra adicionada! O dicionário agradece! 😋');
        setEn(''); setPt('');
        loadPalavras();
      } else {
        showToast(data.message || 'Erro ao adicionar.', 'error');
      }
    } catch { showToast('Erro de conexão.', 'error'); } finally { setLoading(false); }
  }

  // ── Remover palavra ───────────────────────────────────────
  async function removerPalavra(palavraIngles) {
    try {
      const res = await fetch('/user/remover-palavra', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userlogin_id: user.id, palavra_ingles: palavraIngles }),
      });
      const data = await res.json();
      if (data.message?.includes('sucesso')) {
        showToast('Palavra removida.');
        loadPalavras();
      }
    } catch { showToast('Erro ao remover.', 'error'); }
  }

  // ── Quiz ──────────────────────────────────────────────────
  function startQuiz() {
    if (palavras.length < 4) { showToast('Adicione pelo menos 4 palavras para jogar!', 'warning'); return; }
    const qs = shuffle(palavras).slice(0, Math.min(palavras.length, 20));
    setQuizWords(qs);
    setQuizIndex(0);
    setQuizScore(0);
    setQuizAnswered(null);
    setQuizFeedback('');
    setQuizDone(false);
    generateQuizOptions(qs, 0);
  }

  function generateQuizOptions(words, idx) {
    const correct = words[idx];
    const others = shuffle(words.filter((_, i) => i !== idx)).slice(0, 3);
    const opts = shuffle([correct, ...others]);
    setQuizOptions(opts);
    setQuizAnswered(null);
    setQuizFeedback('');
  }

  function answerQuiz(chosen) {
    if (quizAnswered !== null) return;
    const correct = quizWords[quizIndex];
    const isOk = quizMode === 'en→pt'
      ? chosen.palavra_pt === correct.palavra_pt
      : chosen.palavra_ingles === correct.palavra_ingles;

    setQuizAnswered(chosen);
    if (isOk) {
      playSound('/sound/applause.mp3');
      setQuizFeedback('ok');
      setQuizScore(s => s + 1);
    } else {
      playSound('/sound/disappointed.mp3');
      setQuizFeedback('fail');
    }

    setTimeout(() => {
      const next = quizIndex + 1;
      if (next >= quizWords.length) {
        setQuizDone(true);
      } else {
        setQuizIndex(next);
        generateQuizOptions(quizWords, next);
      }
    }, 1300);
  }

  // ── Filtered list ─────────────────────────────────────────
  const filtered = palavras.filter(p =>
    p.palavra_ingles?.toLowerCase().includes(search.toLowerCase()) ||
    p.palavra_pt?.toLowerCase().includes(search.toLowerCase())
  );

  // Hunger meter: 0 = starving, DAILY_LIMIT = full
  const hungerPct = Math.min((todayCount / DAILY_LIMIT) * 100, 100);
  const hungerClass = hungerPct >= 80 ? '' : hungerPct >= 40 ? 'warning' : 'danger';

  // ── Start screen ──────────────────────────────────────────
  if (screen === 'start') return (
    <div className="df-page">
      <div className="df-start">
        <div className="df-dico-img">📖</div>
        <h1>Dicionário Faminto</h1>
        <p>Alimente-me com palavras em inglês e seu vocabulário nunca mais terá fome!</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="df-btn df-btn-primary" onClick={() => setScreen('main')}>
            <i className="fas fa-hamburger" /> Alimentar o Dicionário
          </button>
        </div>
      </div>
    </div>
  );

  // ── Main screen ───────────────────────────────────────────
  const currentQuizWord = quizWords[quizIndex];

  return (
    <div className="df-page">
      <div className="df-card">

        {/* Hunger meter */}
        <div className="df-status">
          <div className="df-hunger-bar">
            <span style={{ fontSize: '1.2rem' }}>
              {hungerPct >= 80 ? '😋' : hungerPct >= 40 ? '😐' : '😩'}
            </span>
            <div className="df-hunger-track">
              <div className={`df-hunger-fill ${hungerClass}`} style={{ width: `${hungerPct}%` }} />
            </div>
          </div>
          <span className="df-words-today">{todayCount}/{DAILY_LIMIT} hoje</span>
        </div>

        {/* Tabs */}
        <div className="df-tabs">
          {[
            { id: 'add',  icon: 'fa-plus',       label: 'Adicionar'  },
            { id: 'list', icon: 'fa-list',        label: `Palavras (${palavras.length})` },
            { id: 'quiz', icon: 'fa-brain',       label: 'Quiz'       },
          ].map(t => (
            <button key={t.id} className={`df-tab${tab === t.id ? ' active' : ''}`}
                    onClick={() => { setTab(t.id); if (t.id === 'quiz') startQuiz(); }}>
              <i className={`fas ${t.icon}`} /> {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Adicionar ── */}
        {tab === 'add' && (
          <form onSubmit={adicionarPalavra} className="df-add-form">
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', textAlign: 'center', fontWeight: 400 }}>
              Adicione palavras em inglês com sua tradução em português.
            </p>
            <div className="df-add-row">
              <input
                className="df-input"
                placeholder="🇬🇧 Palavra em inglês"
                value={en}
                onChange={e => setEn(e.target.value)}
                required
              />
              <input
                className="df-input"
                placeholder="🇧🇷 Tradução em português"
                value={pt}
                onChange={e => setPt(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="df-btn df-btn-green" disabled={loading || todayCount >= DAILY_LIMIT}>
              <i className="fas fa-plus" />
              {loading ? 'Adicionando...' : todayCount >= DAILY_LIMIT ? 'Limite atingido!' : 'Adicionar ao Dicionário'}
            </button>

            {palavras.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 14 }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center', marginBottom: 8 }}>
                  Últimas palavras adicionadas
                </p>
                {palavras.slice(0, 3).map((p, i) => (
                  <div key={i} className="df-word-item">
                    <span className="df-word-en">{p.palavra_ingles}</span>
                    <span className="df-word-arrow">→</span>
                    <span className="df-word-pt">{p.palavra_pt}</span>
                  </div>
                ))}
              </div>
            )}
          </form>
        )}

        {/* ── TAB: Lista ── */}
        {tab === 'list' && (
          <>
            <input
              className="df-search"
              placeholder="🔍 Buscar palavra..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {filtered.length === 0 ? (
              <div className="df-empty">
                <i className="fas fa-book-open" />
                <p>{palavras.length === 0 ? 'Adicione palavras na aba "Adicionar".' : 'Nenhuma palavra encontrada.'}</p>
              </div>
            ) : (
              <div className="df-word-list">
                {filtered.map((p, i) => (
                  <div key={i} className="df-word-item">
                    <span className="df-word-en">{p.palavra_ingles}</span>
                    <span className="df-word-arrow"><i className="fas fa-arrow-right" /></span>
                    <span className="df-word-pt">{p.palavra_pt}</span>
                    {p.data_adicao && (
                      <span className="df-word-date">{timeAgo(p.data_adicao)}</span>
                    )}
                    <button className="df-del-btn" onClick={() => removerPalavra(p.palavra_ingles)} title="Remover">
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Quiz ── */}
        {tab === 'quiz' && (
          <>
            {palavras.length < 4 ? (
              <div className="df-empty">
                <i className="fas fa-brain" />
                <p>Adicione pelo menos 4 palavras para jogar o quiz!</p>
              </div>
            ) : quizDone ? (
              <div className="df-quiz-card">
                <div style={{ fontSize: '3rem', marginBottom: 10 }}>
                  {quizScore >= quizWords.length * 0.7 ? '🏆' : '💪'}
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                  {quizScore}/{quizWords.length}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 20, fontWeight: 400 }}>
                  {quizScore >= quizWords.length * 0.8 ? 'Excelente! 🎉' : quizScore >= quizWords.length * 0.5 ? 'Bom trabalho! Continue praticando.' : 'Continue estudando!'}
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button className="df-btn df-btn-green" onClick={startQuiz}>
                    <i className="fas fa-redo" /> Jogar de novo
                  </button>
                  <button className="df-btn df-btn-ghost"
                          onClick={() => setQuizMode(m => m === 'en→pt' ? 'pt→en' : 'en→pt')}>
                    <i className="fas fa-exchange-alt" /> Inverter modo
                  </button>
                </div>
              </div>
            ) : currentQuizWord ? (
              <div className="df-quiz-card">
                {/* Mode toggle */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button className="df-btn df-btn-ghost"
                          style={{ padding: '5px 12px', fontSize: '0.75rem' }}
                          onClick={() => { setQuizMode(m => m === 'en→pt' ? 'pt→en' : 'en→pt'); startQuiz(); }}>
                    <i className="fas fa-exchange-alt" />
                    {quizMode === 'en→pt' ? 'EN → PT' : 'PT → EN'}
                  </button>
                </div>

                {/* Progress */}
                <div className="df-quiz-progress">
                  <div className="df-quiz-progress-bar">
                    <div className="df-quiz-progress-fill"
                         style={{ width: `${(quizIndex / quizWords.length) * 100}%` }} />
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                    {quizIndex + 1}/{quizWords.length}
                  </span>
                </div>

                <div className="df-quiz-prompt">
                  {quizMode === 'en→pt' ? 'Como se diz em português?' : 'Como se diz em inglês?'}
                </div>

                <div className="df-quiz-word">
                  {quizMode === 'en→pt' ? currentQuizWord.palavra_ingles : currentQuizWord.palavra_pt}
                </div>

                <div className="df-quiz-options">
                  {quizOptions.map((opt, i) => {
                    const displayText = quizMode === 'en→pt' ? opt.palavra_pt : opt.palavra_ingles;
                    const isCorrectOpt = quizMode === 'en→pt'
                      ? opt.palavra_pt === currentQuizWord.palavra_pt
                      : opt.palavra_ingles === currentQuizWord.palavra_ingles;
                    const isChosen = quizAnswered &&
                      (quizMode === 'en→pt' ? opt.palavra_pt === quizAnswered.palavra_pt : opt.palavra_ingles === quizAnswered.palavra_ingles);

                    let cls = '';
                    if (quizAnswered !== null) {
                      if (isCorrectOpt) cls = 'correct';
                      else if (isChosen && !isCorrectOpt) cls = 'wrong';
                    }

                    return (
                      <button key={i} className={`df-quiz-opt ${cls}`}
                              onClick={() => answerQuiz(opt)}
                              disabled={quizAnswered !== null}>
                        {displayText}
                      </button>
                    );
                  })}
                </div>

                <div className={`df-quiz-feedback${quizFeedback ? ` ${quizFeedback}` : ''}`}>
                  {quizFeedback === 'ok'   && `✓ Correto! +1 ponto`}
                  {quizFeedback === 'fail' && `✗ Era: "${quizMode === 'en→pt' ? currentQuizWord.palavra_pt : currentQuizWord.palavra_ingles}"`}
                </div>

                <div style={{ textAlign: 'right', color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem' }}>
                  Pontuação: {quizScore}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      {toast && <div className={`df-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
